import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogOut, Trash2, UploadCloud, ArrowLeft, KeyRound } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  "Corporate Gifting & Merchandise",
  "Corporate Events & Experiences",
  "Exhibition & Expo Solutions",
  "Corporate Media & Content Production",
  "Branding, Creative, Printing & Production",
  "Corporate Travel & Destination Management",
  "Employee Engagement & Internal Communications",
  "Brand Activations & Experiential Marketing",
];

const inputCls =
  "w-full rounded-xl border border-black/15 bg-white px-4 py-3 text-sm text-royal-deep placeholder:text-black/35 outline-none transition-all duration-300 focus:border-gold focus:shadow-[0_0_0_3px_rgba(224,19,39,0.15)]";

function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AdminPanel() {
  const [user, setUser] = useState(undefined);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", category: "", description: "" });
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [busyPw, setBusyPw] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);

  const tryRefresh = async () => {
    try {
      const r = await fetch(`${API}/auth/refresh`, { method: "POST", credentials: "include" });
      return r.ok;
    } catch {
      return false;
    }
  };

  const loadMe = async () => {
    try {
      let r = await fetch(`${API}/auth/me`, { credentials: "include" });
      if (r.status === 401 && (await tryRefresh())) {
        r = await fetch(`${API}/auth/me`, { credentials: "include" });
      }
      setUser(r.ok ? await r.json() : null);
    } catch {
      setUser(null);
    }
  };

  const loadItems = async () => {
    try {
      const r = await fetch(`${API}/gallery`);
      const d = await r.json();
      setItems(Array.isArray(d) ? d : []);
    } catch {
      setItems([]);
    }
  };

  useEffect(() => {
    loadMe();
    loadItems();
  }, []);

  const login = async (e) => {
    e.preventDefault();
    if (loggingIn) return;
    setLoggingIn(true);
    try {
      const r = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(creds),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(formatApiError(data.detail));
      setUser(data);
      toast.success("Welcome back.");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = async () => {
    await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
    setUser(null);
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (busyPw) return;
    if (pw.next !== pw.confirm) return toast.error("New passwords do not match");
    if (pw.next.length < 8) return toast.error("New password must be at least 8 characters");
    setBusyPw(true);
    try {
      const call = () =>
        fetch(`${API}/auth/change-password`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ current_password: pw.current, new_password: pw.next }),
        });
      let r = await call();
      if (r.status === 401 && (await tryRefresh())) r = await call();
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(formatApiError(data.detail));
      toast.success("Password changed. Use the new one next time you sign in.");
      setPw({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusyPw(false);
    }
  };

  const upload = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!file) return toast.error("Choose an image first");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", form.title);
    fd.append("category", form.category);
    fd.append("description", form.description);
    setBusy(true);
    try {
      const call = () => fetch(`${API}/gallery`, { method: "POST", body: fd, credentials: "include" });
      let r = await call();
      if (r.status === 401 && (await tryRefresh())) r = await call();
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(formatApiError(data.detail));
      toast.success("Uploaded to the showcase.");
      setForm({ title: "", category: "", description: "" });
      setFile(null);
      const input = document.getElementById("admin-file-input");
      if (input) input.value = "";
      loadItems();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      const call = () => fetch(`${API}/gallery/${id}`, { method: "DELETE", credentials: "include" });
      let r = await call();
      if (r.status === 401 && (await tryRefresh())) r = await call();
      if (!r.ok) throw new Error("Could not delete. Please try again.");
      setItems((prev) => prev.filter((x) => x.id !== id));
      toast.success("Removed from the showcase.");
    } catch (err) {
      toast.error(err.message);
    }
  };

  if (user === undefined) {
    return (
      <div className="min-h-screen bg-white grid place-items-center text-black/30 font-body">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-royal-soft grid place-items-center px-5 font-body">
        <form
          onSubmit={login}
          data-testid="admin-login-form"
          className="w-full max-w-md rounded-3xl border border-black/10 bg-white p-8 shadow-[0_24px_70px_rgba(0,0,0,0.12)]"
        >
          <img src="/assets/oec-logo-brand.png" alt="One Energy Club" className="h-12 w-auto" />
          <h1 className="mt-6 font-display text-2xl font-bold text-royal-deep">Work Showcase admin</h1>
          <p className="mt-2 text-sm text-black/65">Sign in to upload photos and case studies.</p>

          <div className="mt-7 space-y-4">
            <input
              type="email" required placeholder="Admin email" data-testid="admin-email-input"
              className={inputCls} value={creds.email}
              onChange={(e) => setCreds({ ...creds, email: e.target.value })}
            />
            <input
              type="password" required placeholder="Password" data-testid="admin-password-input"
              className={inputCls} value={creds.password}
              onChange={(e) => setCreds({ ...creds, password: e.target.value })}
            />
          </div>

          <button
            type="submit" disabled={loggingIn}
            data-testid="admin-login-button"
            className="mt-6 inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-gold px-8 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-gold-dark disabled:opacity-60"
          >
            {loggingIn ? <Loader2 size={16} className="animate-spin" /> : "Sign in"}
          </button>

          <Link to="/" data-testid="admin-back-to-site" className="mt-6 inline-flex items-center gap-2 text-xs text-black/50 hover:text-royal-deep transition-colors">
            <ArrowLeft size={13} /> Back to website
          </Link>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-royal font-body">
      <div className="mx-auto max-w-6xl px-5 sm:px-8 py-10">
        <header className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <img src="/assets/oec-logo-brand.png" alt="One Energy Club" className="h-11 w-auto" />
            <span className="hidden sm:inline-block rounded-full border border-gold/40 bg-gold-soft px-3 py-1 font-mono text-[10px] tracking-[0.2em] uppercase text-gold-dark">
              Showcase admin
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="admin-view-site-link" className="text-xs text-black/60 hover:text-royal-deep transition-colors">
              View site
            </Link>
            <button
              onClick={logout} data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 rounded-full border border-black/15 px-4 py-2 text-xs font-medium text-black/70 hover:border-gold hover:text-gold-dark transition-all duration-300"
            >
              <LogOut size={13} /> Sign out
            </button>
          </div>
        </header>

        <form
          onSubmit={upload} data-testid="admin-upload-form"
          className="mt-10 rounded-3xl border border-black/10 bg-royal-soft p-6 sm:p-8"
        >
          <h2 className="font-display text-xl font-semibold text-royal-deep">Upload to the showcase</h2>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="md:col-span-2">
              <label className="block text-[13px] font-medium text-black/80 mb-2" htmlFor="admin-file-input">
                Event photo <span className="text-gold-dark">*</span> <span className="text-black/50 font-normal">(JPG, PNG or WebP — up to 8 MB)</span>
              </label>
              <input
                id="admin-file-input" type="file" accept="image/jpeg,image/png,image/webp" required
                data-testid="admin-file-input"
                onChange={(e) => setFile(e.target.files[0])}
                className="w-full rounded-xl border border-black/15 bg-white px-4 py-2.5 text-sm text-black/70 file:mr-4 file:rounded-full file:border-0 file:bg-gold file:px-4 file:py-1.5 file:text-xs file:font-semibold file:text-white hover:file:bg-gold-dark"
              />
            </div>
            <div>
              <label className="block text-[13px] font-medium text-black/80 mb-2" htmlFor="admin-title-input">Title <span className="text-gold-dark">*</span></label>
              <input
                id="admin-title-input" required placeholder="e.g. Annual Leadership Summit 2026" data-testid="admin-title-input"
                className={inputCls} value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-[13px] font-medium text-black/80 mb-2" htmlFor="admin-category-select">Service category</label>
              <select
                id="admin-category-select" data-testid="admin-category-select"
                className={`${inputCls} appearance-none`} value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
              >
                <option value="">Select a service</option>
                {SERVICES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-[13px] font-medium text-black/80 mb-2" htmlFor="admin-description-input">Case study note</label>
              <textarea
                id="admin-description-input" rows={3} data-testid="admin-description-input"
                placeholder="What was the brief, and what did the team deliver?"
                className={`${inputCls} resize-y`} value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>
          </div>

          <button
            type="submit" disabled={busy}
            data-testid="admin-upload-button"
            className="mt-6 inline-flex items-center gap-2.5 rounded-full bg-gold px-8 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-gold-dark hover:shadow-[0_8px_32px_rgba(224,19,39,0.35)] disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {busy ? (
              <>
                Uploading <Loader2 size={16} className="animate-spin" />
              </>
            ) : (
              <>
                <UploadCloud size={17} /> Upload to showcase
              </>
            )}
          </button>
        </form>

        <form
          onSubmit={changePassword} data-testid="admin-password-form"
          className="mt-6 rounded-3xl border border-black/10 bg-white p-6 sm:p-8 shadow-[0_10px_40px_rgba(0,0,0,0.07)]"
        >
          <h2 className="font-display text-xl font-semibold text-royal-deep flex items-center gap-2.5">
            <KeyRound size={18} className="text-gold-dark" />
            Change admin password
          </h2>
          <p className="mt-1.5 text-[13px] text-black/55">
            Set your own password — it stays saved on the live site even after restarts and redeploys.
          </p>
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <input
              type="password" required placeholder="Current password" data-testid="admin-current-password-input"
              className={inputCls} value={pw.current}
              onChange={(e) => setPw({ ...pw, current: e.target.value })}
            />
            <input
              type="password" required placeholder="New password (min 8 chars)" data-testid="admin-new-password-input"
              className={inputCls} value={pw.next}
              onChange={(e) => setPw({ ...pw, next: e.target.value })}
            />
            <input
              type="password" required placeholder="Confirm new password" data-testid="admin-confirm-password-input"
              className={inputCls} value={pw.confirm}
              onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
            />
          </div>
          <button
            type="submit" disabled={busyPw}
            data-testid="admin-change-password-button"
            className="mt-5 inline-flex items-center gap-2.5 rounded-full border border-gold/60 bg-white px-6 py-3 text-sm font-semibold text-royal-deep transition-all duration-300 hover:bg-gold hover:text-white disabled:opacity-60"
          >
            {busyPw ? <Loader2 size={15} className="animate-spin" /> : <KeyRound size={15} />}
            Update password
          </button>
        </form>

        <h2 className="mt-12 font-display text-xl font-semibold text-royal-deep">
          Live showcase <span className="text-black/50 font-normal text-sm">({items.length})</span>
        </h2>
        {items.length === 0 ? (
          <p className="mt-4 text-sm text-black/50" data-testid="admin-empty-list">
            Nothing uploaded yet. Your first upload will appear on the website instantly.
          </p>
        ) : (
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 pb-16">
            {items.map((item) => (
              <div
                key={item.id} data-testid={`admin-gallery-item-${item.id.slice(0, 8)}`}
                className="rounded-2xl border border-black/10 bg-white overflow-hidden shadow-[0_6px_24px_rgba(0,0,0,0.07)]"
              >
                <img
                  src={`${API}/gallery/image/${item.id}`} alt={item.title}
                  className="h-40 w-full object-cover"
                />
                <div className="p-4">
                  <p className="font-display text-sm font-semibold text-royal-deep truncate">{item.title}</p>
                  {item.category && (
                    <p className="mt-1 font-mono text-[10px] tracking-[0.2em] uppercase text-gold-dark truncate">{item.category}</p>
                  )}
                  <button
                    onClick={() => remove(item.id)} data-testid={`admin-delete-item-${item.id.slice(0, 8)}`}
                    className="mt-3 inline-flex items-center gap-2 text-xs text-black/60 hover:text-red-600 transition-colors duration-300"
                  >
                    <Trash2 size={13} /> Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
